import { BaseApi, ApiResult } from './BaseApi';
import Settings from '@/models/Settings';
import * as ApiInterfaces from '@/models/Interfaces';
import { DataStructure, DataStructureField } from '@/models/DataStructure';
import { FormField, LandingPagesFilter, LandingPagesFilterResult, Project, Submission } from '@/models/Project';
import { Newsletter, NewsletterSaveRequest, NewslettersFilter, NewslettersFilterResult } from '@/models/Newsletter';
import { MainPageResponse } from '@/models/MainPage';
import {
    ContactFilter,
    ContactFilterResult,
    ContactHistoryFilter,
    ContactHistoryResult,
    ContactResource,
    ContactSaveResource,
    ContactCreateRequest,
    ContactGroupFilter,
    ContactGroupFilterResult,
    GroupSaveResource,
    TagSaveRequest,
    CustomListSaveResource,
    ContactCustomListFilterResult,
    ContactCustomListsFilter,
    ContactCustomListValuesQueryResult,
    CustomListValueSaveResource,
} from '@/models/Contacts';
import * as IEmail from '@/models/Emails';
import {
    AutomationDataResponse,
    AutomationConditionResponse,
    AutomationFilter,
    AutomationFilterResult,
    AutomationRow,
    AutomationTriggerEnum,
    AutomationTriggerRequest,
    AutomationTriggerResource,
    AutomationTriggerShortResource,
    ExactAutomation,
    IfElseModel,
    SaveAutomationNotificationRequest,
    SaveAutomationSendSmsRequest,
    SendEmailActionRequest,
    StepUpdateTypeEnum,
    WebhookActionParams,
} from '@/models/Automations';
import { ContactFilterFieldEnum } from '@/models/enums';
import {
    SendSmsModel,
    SendTestSmsModel,
    SMSBatchHistoryResult,
    SMSProfileResource,
    SMSProfilesResult,
    SMSsHistoryFilter,
    SMSsProfilesFilter,
    SMSTemplateResponse,
    EmailSMSTemplateRowResource,
    SMSTemplatesResult,
    SmsTemplateFilter,
} from '@/models/SMS';
import {
    GetTaskInfoResponse,
    GetTaskProgressRequest,
    GetTaskProgressResponse,
    ValidateImportRequest,
    ImportValidationResponse,
    VrmFieldsForSyncAppResponse,
} from '@/models/Import';
import { EmailSendingSettings, SendTestEmailModel } from '@/models/Emails';
import { ClientSettings, SyncAppSettings } from '@/models/ClientSettings';
import { Dictionary } from 'lodash';

// prettier-ignore
export class Api extends BaseApi {
    public async Init(): Promise<ApiResult<ApiInterfaces.InitResponse>> {
        return this.get<ApiInterfaces.InitResponse>('api/init');
    }
    public async Login(username: string, password: string): Promise<ApiResult<ApiInterfaces.UserState>> {
        return this.post<ApiInterfaces.UserState>('api/authenticate', JSON.stringify({ UserName: username, Password: password }));
    }
    public async LogOut(): Promise<ApiResult<void>> {
        return this.get<void>('auth/logout');
    }
    public async ChangeLanguage(lang: string): Promise<ApiResult<ApiInterfaces.ChangeLanguage>> {
        return this.post<ApiInterfaces.ChangeLanguage>('api/change-language', JSON.stringify({ Lang: lang }));
    }
    /** MainPage */
    public async GetMainPageData(): Promise<ApiResult<MainPageResponse>> {
        return this.get<MainPageResponse>('api/home/init');
    }
    public async GetPage(id: string): Promise<ApiResult<Project>> {
        return this.get<Project>('api/page/' + id);
    }
    public async GetNewsletter(id: string): Promise<ApiResult<Newsletter>> {
        return this.get<Newsletter>('api/newsletter/' + id);
    }
    public async GetNewsletters(filter: NewslettersFilter): Promise<ApiResult<NewslettersFilterResult>> {
        return this.post<NewslettersFilterResult>('api/newsletters/filter', JSON.stringify({ filter }));
    }
    public async SaveTextNewsletter(template: Newsletter): Promise<ApiResult<Newsletter>> {
        return this.put<Newsletter>('api/save-text-newsletter', JSON.stringify(template));
    }
    public async GetNewsletterForPage(id: string, pageId: string): Promise<ApiResult<Newsletter>> {
        return this.get<Newsletter>(`api/newsletter/${id}/${pageId}`);
    }
    public async GetPageSettings(id: string): Promise<ApiResult<Project>> {
        return this.get<Project>('api/page-settings/' + id);
    }
    public async SavePageSettings(settings: Settings): Promise<ApiResult<Project>> {
        return this.post<Project>('api/page-settings', JSON.stringify(settings));
    }
    public async AddPage(pageName: string, templateId: number): Promise<ApiResult<Project>> {
        return this.put<Project>('api/page/' + templateId, JSON.stringify(pageName));
    }
    public async AddNewsletter(newsletter: NewsletterSaveRequest): Promise<ApiResult<Newsletter>> {
        return this.put<Newsletter>('api/newsletter', JSON.stringify(newsletter));
    }
    public async SaveNewsletterFieldValues(pageId: string, newsletter: Newsletter): Promise<ApiResult<void>> {
        return this.post<void>('api/newsletter-field-values/' + pageId, JSON.stringify(newsletter));
    }
    public async DeleteNewsletter(id: string): Promise<ApiResult<number>> {
        return this.delete<number>('api/newsletter/' + id);
    }
    public async DeleteProject(id: string): Promise<ApiResult<number>> {
        return this.delete<number>('api/page/' + id);
    }
    public async DeployProject(id: string): Promise<ApiResult<ApiInterfaces.PagePublishResponse>> {
        return this.post<ApiInterfaces.PagePublishResponse>('api/page-publish/' + id);
    }
    public async PageName(id: string, name: string): Promise<ApiResult<void>> {
        return this.post('api/page-name/' + id, JSON.stringify(name));
    }
    public async NewsletterName(id: string, name: string): Promise<ApiResult<void>> {
        return this.post('api/newsletter-name/' + id, JSON.stringify(name));
    }
    public async PageClone(id: string, name: string): Promise<ApiResult<Project>> {
        return this.post<Project>('api/page-clone/' + id, JSON.stringify(name));
    }
    public async NewsletterClone(id: string, name: string): Promise<ApiResult<Newsletter>> {
        return this.post<Newsletter>('api/newsletter-clone/' + id, JSON.stringify(name));
    }
    public async GetDataStructures(): Promise<ApiResult<DataStructure[]>> {
        return this.get<DataStructure[]>('api/data-structures');
    }
    public async StartTemplates(): Promise<ApiResult<ApiInterfaces.StartTemplate[]>> {
        return this.get<ApiInterfaces.StartTemplate[]>('api/startTemplates');
    }
    public async ParseDataStructureFields(formData: FormData): Promise<ApiResult<DataStructureField[]>> {
        return this.postFormData<DataStructureField[]>('api/parseDataStructureFields', formData);
    }
    public async SaveDataStructure(dataStructure: DataStructure): Promise<ApiResult<DataStructure>> {
        return this.post<DataStructure>('api/data-structure', JSON.stringify(dataStructure));
    }
    public async DeleteDataStructure(Id: number): Promise<ApiResult<number>> {
        return this.delete<number>('api/data-structure/' + Id);
    }
    public async UploadImage(formData: FormData, Id: string): Promise<ApiResult<string>> {
        return this.postFormData<string>('api/upload-image/' + Id, formData);
    }
    public async UploadNewsletterImage(formData: FormData, Id: string): Promise<ApiResult<string>> {
        return this.postFormData<string>('api/upload-newsletter-image/' + Id, formData);
    }
    public async SaveFormFields(fields: FormField[], id: string): Promise<ApiResult<FormField[]>> {
        return this.post<FormField[]>('api/form-fields/' + id, JSON.stringify(fields));
    }
    public async GetLandingPages(filter: LandingPagesFilter): Promise<ApiResult<LandingPagesFilterResult>> {
        return this.post<LandingPagesFilterResult>('api/landing-pages/filter', JSON.stringify({ filter }));
    }
    public async GetPageSubmissions(id: string): Promise<ApiResult<Project>> {
        return this.get<Project>('api/page-submissions/' + id);
    }
    public async GetPageSubmissionsXLS(id: string): Promise<boolean> {
        return this.getFile('api/export/page-submissions/' + id);
    }
    public async ChangePageTemplate(pageId: string, templateId: number): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/page-template-change/' + pageId, JSON.stringify(templateId));
    }
    public async FormEmbedCode(pageId: string): Promise<ApiResult<string>> {
        return this.get<string>('api/form-embed-code/' + pageId);
    }
    public async EncryptParams(pageId: string, params: string): Promise<ApiResult<string>> {
        return this.post<string>('api/encrypt-params/' + pageId, JSON.stringify(params));
    }
    public async CreateForm(pageId: string, params = ''): Promise<ApiResult<string>> {
        return this.get<string>('api/create-form/' + pageId + (params ? '?id=' + params : ''));
    }
    public async ParseSwaggerDataStructure(id: number): Promise<ApiResult<DataStructure>> {
        return this.get<DataStructure>('api/parse-swagger-data-structure/' + id);
    }
    public async ParseSwaggerDataStructureFromUrl(url: string): Promise<ApiResult<DataStructure>> {
        return this.post<DataStructure>('api/parse-swagger-data-structure-from-url', JSON.stringify(url));
    }
    public async ReloadDataStructureOptions(url: string): Promise<ApiResult<DataStructureField[]>> {
        return this.post<DataStructureField[]>('api/reload-data-structure-options-from-url', JSON.stringify(url));
    }
    public async PagePreview(pageId: string, template: unknown): Promise<ApiResult<string>> {
        return this.post<string>('api/page-preview/' + pageId, JSON.stringify(template));
    }

    public async ResendSubmission(id: number): Promise<ApiResult<Submission>> {
        return this.get<Submission>('api/resend-submission/' + id);
    }

    //Filter Contacts
    public async FilterContacts(filter: ContactFilter, pageIndex = 0): Promise<ApiResult<ContactFilterResult>> {
        return this.post<ContactFilterResult>('api/contacts/filter', JSON.stringify({ filter, pageIndex }));
    }
    // Group API
    public async FilterGroups(filter: ContactGroupFilter, pageIndex = 0): Promise<ApiResult<ContactGroupFilterResult>> {
        return this.post<ContactGroupFilterResult>('api/groups/filter', JSON.stringify({ filter, pageIndex }));
    }
    public async DeleteGroup(ids: number[], ignoreWarning = false): Promise<ApiResult<boolean>> {
        return this.delete<boolean>('api/groups', { data: { id: ids, ignoreWarning } });
    }
    public async ConnectContactsToGroups(contacts: number[], groups: number[]): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/groups/connect', JSON.stringify({ contacts, groups }));
    }
    public async CreateEditGroup(group: GroupSaveResource): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/groups', JSON.stringify({ group }));
    }
    public async UnconnectContactsToGroups(contacts: number[], groups: number[]): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/groups/unconnect', JSON.stringify({ contacts, groups }));
    }
    public async MoveContactsToGroups(contacts: number[], groups: number[]): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/groups/move', JSON.stringify({ contacts, groups }));
    }

    // Custom List API
    public async CreateEditCustomList(customList: CustomListSaveResource): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/custom-lists', JSON.stringify({ customList }));
    }
    public async FilterCustomLists(filter: ContactCustomListsFilter, pageIndex = 0): Promise<ApiResult<ContactCustomListFilterResult>> {
        return this.get<ContactCustomListFilterResult>('api/custom-lists/filter', { params: { ...filter, pageIndex } });
    }
    public async AddCustomListValue(value: CustomListValueSaveResource): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/custom-lists/values', JSON.stringify({ customListValue: value }));
    }
    public async GetCustomListValues(customListId: number): Promise<ApiResult<ContactCustomListValuesQueryResult>> {
        return this.get<ContactCustomListValuesQueryResult>('api/custom-lists/values', { params: { customListId } });
    }

    // Automations
    public async FilterAutomations(filter: AutomationFilter, pageIndex = 0, pageSize = 50): Promise<ApiResult<AutomationFilterResult>> {
        return this.post<AutomationFilterResult>('api/automations/filter', JSON.stringify({ filter, pageIndex, pageSize }));
    }
    public async GetAutomation(id: number): Promise<ApiResult<ExactAutomation>> {
        return this.get<ExactAutomation>('api/automations', { params: { id } });
    }
    public async CreateAutomation(name: string): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/create', JSON.stringify({ name }));
    }
    public async SaveAutomation(automation: ExactAutomation): Promise<ApiResult<ExactAutomation>> {
        return this.post<ExactAutomation>('api/automations', JSON.stringify(automation));
    }
    public async CopyAutomation(id: number): Promise<ApiResult<AutomationRow>> {
        return this.post<AutomationRow>('api/automations/copy', JSON.stringify({ id }));
    }
    public async PauseNewContacts(id: string, newState: boolean): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/automations/pause', JSON.stringify({ id, newState }));
    }
    public async ValidateAutomation(automationId: number, edges: any[]): Promise<ApiResult<boolean>> {
        return this.patch<boolean>('api/automations/validate', JSON.stringify({ automationId, edges }));
    }
    public async StartAutomation(id: number): Promise<ApiResult<boolean>> {
        return this.patch<boolean>('api/automations/start', JSON.stringify({ id }));
    }
    public async DeleteAutomation(ids: number[]): Promise<ApiResult<boolean>> {
        return this.delete<boolean>('api/automations', { data: { id: ids } });
    }
    public async StopAutomation(id: number): Promise<ApiResult<string>> {
        return this.post<string>('api/automations/stop', JSON.stringify({ id }));
    }
    public async SaveAutomationJson(id: number, json: string, edges: any[], stepsId: number[]): Promise<ApiResult<AutomationDataResponse>> {
        return this.post<AutomationDataResponse>(
            'api/automations/steps',
            JSON.stringify({ AutomationId: id, JsonData: json, edges, stepsId })
        );
    }
    public async AddContactToAutomation(automationId: number, contactId: number): Promise<ApiResult<boolean>> {
        return this.patch<boolean>('api/automations/add-contact', JSON.stringify({ automationId, contactId }));
    }
    public async GetAutomationSteps(id: number): Promise<ApiResult<AutomationDataResponse>> {
        return this.get<AutomationDataResponse>('api/automations/steps', { params: { automationId: id } });
    }
    public async GetExactAutomationStep(stepId: number): Promise<ApiResult<AutomationConditionResponse>> {
        return this.get<AutomationConditionResponse>('api/automations/steps/get', { params: { stepId: stepId } });
    }
    public async SaveTrigger(request: AutomationTriggerRequest): Promise<ApiResult<AutomationTriggerShortResource>> {
        return this.post<AutomationTriggerShortResource>('api/automations/trigger', JSON.stringify(request));
    }
    public async GetTrigger(id: number, automationId: number): Promise<ApiResult<AutomationTriggerResource>> {
        return this.get<AutomationTriggerResource>('api/automations/trigger', { params: { id, automationId } });
    }
    public async SaveSendSmsAction(req: SaveAutomationSendSmsRequest): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/steps/save/sms-step', JSON.stringify(req));
    }
    public async SaveSendEmailAction(data: SendEmailActionRequest): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/steps/save/email-step', JSON.stringify(data));
    }
    public async SaveNotificationAction(req: SaveAutomationNotificationRequest): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/steps/save/send-notification', JSON.stringify(req));
    }
    public async SaveUpdateContactAction(
        AutomationId: number,
        StepId: number,
        UpdateType: StepUpdateTypeEnum,
        Value?: string | number | null,
        GroupId?: number | null,
        TagId?: number | null,
        Field?: ContactFilterFieldEnum | number | null
    ): Promise<ApiResult<number>> {
        return this.post<number>(
            'api/automations/steps/save/update-contact',
            JSON.stringify({ AutomationId, StepId, UpdateType, Value, GroupId, TagId, Field })
        );
    }
    public async SaveStartAnotherAutomationAction(
        AutomationId: number,
        StepId: number,
        NextAutomationId: number,
        IfPaused: number
    ): Promise<ApiResult<number>> {
        return this.post<number>(
            'api/automations/steps/save/start-automation-step',
            JSON.stringify({ AutomationId, StepId, NextAutomationId, IfPaused })
        );
    }
    // TODO : change code on boack and params here
    public async SaveSendWhatsAppAction(
        AutomationId: number,
        StepId: number,
        NextAutomationId: number,
        IfPaused: number
    ): Promise<ApiResult<number>> {
        return this.post<number>(
            'api/automations/steps/save/whatsapp-step',
            JSON.stringify({ AutomationId, StepId, NextAutomationId, IfPaused })
        );
    }
    public async SaveSendNotificationAction(
        AutomationId: number,
        StepId: number,
        NextAutomationId: number,
        IfPaused: number
    ): Promise<ApiResult<number>> {
        return this.post<number>(
            'api/automations/steps/save/send-notification',
            JSON.stringify({ AutomationId, StepId, NextAutomationId, IfPaused })
        );
    }
    public async SaveUnsubscribeAction(
        AutomationId: number,
        StepId: number,
        FromSms: boolean,
        FromEmail: boolean
    ): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/steps/save/unsubscribe', JSON.stringify({ AutomationId, StepId, FromSms, FromEmail }));
    }
    public async SaveRemoveContactAction(AutomationId: number, StepId: number): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/steps/save/remove-contact', JSON.stringify({ AutomationId, StepId }));
    }
    public async SaveWebhookAction(
        AutomationId: number,
        StepId: number,
        Url: string,
        ParamsType: number,
        Params: WebhookActionParams[]
    ): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/steps/save/webhook', JSON.stringify({ AutomationId, StepId, Url, ParamsType, Params }));
    }
    public async SaveSplitCondition(
        AutomationId: number,
        StepId: number,
        SplitType: number,
        Value: number,
        Remarks: string
    ): Promise<ApiResult<number>> {
        return this.post<number>(
            'api/automations/steps/save/split-step',
            JSON.stringify({ AutomationId, StepId, SplitType, Value, Remarks })
        );
    }
    public async SaveIfElseCondition(req: IfElseModel): Promise<ApiResult<number>> {
        return this.post<number>('api/automations/steps/save/condition-step', JSON.stringify(req));
    }
    //Contact
    public async GetContact(id: number): Promise<ApiResult<ContactResource>> {
        return this.get<ContactResource>('api/contacts', { params: { id } });
    }
    public async SaveContact(contact: ContactSaveResource): Promise<ApiResult<ContactResource>> {
        return this.post<ContactResource>('api/contacts', JSON.stringify({ contact }));
    }
    public async CreateContact(contact: ContactCreateRequest): Promise<ApiResult<ContactCreateRequest>> {
        return this.post<ContactCreateRequest>('api/contacts/create', JSON.stringify({ contact }));
    }
    public async GetContactFields() {
        return this.get<ApiInterfaces.ContactFieldsMappingResponse>('api/contacts/fields-mapping');
    }
    public async SaveContactFields(req: ApiInterfaces.SaveContactFieldsMappingRequest) {
        return this.post<ApiInterfaces.ContactFieldsMappingResponse>('api/contacts/fields-mapping', JSON.stringify(req));
    }
    public async FilterContactHistory(id: number, filter: ContactHistoryFilter, lastId = 0): Promise<ApiResult<ContactHistoryResult>> {
        return this.post<ContactHistoryResult>('api/contacts/history', JSON.stringify({ id, filter, lastId }));
    }
    public async DeleteContacts(ids: number[]): Promise<ApiResult<boolean>> {
        return this.delete<boolean>('api/contacts', { data: { id: ids } });
    }
    public async UnsubscribeCardContact(id: number, unsubscribeEmail: boolean, unsubscribeSMS: boolean): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/contacts/card/unsubscribe', JSON.stringify({ id, unsubscribeEmail, unsubscribeSMS }));
    }
    public async UnsubscribeContacts(ids: number[], unsubscribeEmail: boolean, unsubscribeSMS: boolean): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/contacts/unsubscribe', JSON.stringify({ ids, unsubscribeEmail, unsubscribeSMS }));
    }
    /**
     * Export status
     * @param jobId - export job id
     * @returns job id
     */
    public async ExportStatus(jobId: string): Promise<ApiResult<ApiInterfaces.ExportStatus>> {
        return this.get<ApiInterfaces.ExportStatus>(`api/export/status/${jobId}`);
    }

    /**
     * Emails History
     * @param lastId - minimum id from prev search
     * @returns History rows
     */
    public async EmailGetHistory(filter: IEmail.EmailsHistoryFilter, lastId = 0): Promise<ApiResult<IEmail.EmailBatchHistoryResult>> {
        return this.post<IEmail.EmailBatchHistoryResult>('api/email/history/filter', JSON.stringify({ filter, lastId }));
    }

    /**
     * Emails History details export
     * @param batchId - sending id
     * @returns job id
     */
    public async EmailExportHistoryDetail(batchId: number): Promise<ApiResult<string>> {
        return this.post<string>(`api/email/history/export-details/${batchId}`);
    }

    /**
     * Emails Profiles
     * @param pageIndex - page index to load next records
     * @returns Email profiles rows
     */
    public async EmailGetProfiles(filter: IEmail.EmailsProfilesFilter, pageIndex = 0): Promise<ApiResult<IEmail.EmailProfilesResult>> {
        return this.post<IEmail.EmailProfilesResult>('api/settings/email-profile/filter', JSON.stringify({ filter, pageIndex }));
    }
    /**
     * Get Email Profile
     * @param id - profile Id
     * @returns profile
     */
    public async EmailGetProfile(id: number): Promise<ApiResult<IEmail.EmailProfileResource>> {
        return this.get<IEmail.EmailProfileResource>('api/settings/email-profile', {
            params: { id },
        });
    }
    /**
     * Save Email Profile
     * @param profile - profile to save
     * @returns profile
     */
    public async EmailSaveProfile(profile: IEmail.EmailProfileResource): Promise<ApiResult<IEmail.EmailProfileResource>> {
        return this.post<IEmail.EmailProfileResource>('api/settings/email-profile', JSON.stringify(profile));
    }
    /**
     * Delete Email Profile
     * @param profile - profile Id
     * @returns profile
     */
    public async EmailDeleteProfile(id: number): Promise<ApiResult<boolean>> {
        return this.delete<boolean>(`api/settings/email-profile/${id}`);
    }
    /**
     * SMS History
     * @param lastId - minimum id from prev search
     * @returns History rows
     */
    public async SMSGetHistory(filter: SMSsHistoryFilter, lastId = 0): Promise<ApiResult<SMSBatchHistoryResult>> {
        return this.post<SMSBatchHistoryResult>('api/sms/history/filter', JSON.stringify({ filter, lastId }));
    }
    // ********** SMS ***********

    public async SMSGetProfiles(filter: SMSsProfilesFilter, pageIndex = 0): Promise<ApiResult<SMSProfilesResult>> {
        return this.post<SMSProfilesResult>('api/sms/profile/filter', JSON.stringify({ filter, pageIndex }));
    }
    public async SMSGetProfile(id: number): Promise<ApiResult<SMSProfileResource>> {
        return this.get<SMSProfileResource>('api/sms/profile', {
            params: { id },
        });
    }
    public async SMSGetTemplate(id: string): Promise<ApiResult<SMSTemplateResponse>> {
        return this.get<SMSTemplateResponse>('api/sms/template', {
            params: { id },
        });
    }
    public async SMSSaveProfile(profile: SMSProfileResource): Promise<ApiResult<SMSProfileResource>> {
        return this.post<SMSProfileResource>('api/sms/profile', JSON.stringify(profile));
    }
    public async SMSSaveTemplate(template: EmailSMSTemplateRowResource): Promise<ApiResult<EmailSMSTemplateRowResource>> {
        return this.post<EmailSMSTemplateRowResource>('api/sms/templates', JSON.stringify(template));
    }
    public async SMSCopyTemplate(Id: string): Promise<ApiResult<EmailSMSTemplateRowResource>> {
        return this.post<EmailSMSTemplateRowResource>('api/sms/templates/copy', JSON.stringify(Id));
    }
    public async SMSGetTemplates(filter: SmsTemplateFilter, pageIndex = 0): Promise<ApiResult<SMSTemplatesResult>> {
        return this.post<SMSTemplatesResult>('api/sms/templates/filter', JSON.stringify({ filter, pageIndex }));
    }
    public async SMSGetTemplatesForSelect(): Promise<ApiResult<EmailSMSTemplateRowResource[]>> {
        return this.get<EmailSMSTemplateRowResource[]>('api/sms/templates/filter-all');
    }
    public async SMSDeleteProfile(id: number): Promise<ApiResult<boolean>> {
        return this.delete<boolean>(`api/sms/profile/${id}`);
    }
    public async SMSDeleteTemplate(id: string): Promise<ApiResult<boolean>> {
        return this.delete<boolean>(`api/sms/template/${id}`);
    }
    public async SMSExportHistoryDetail(batchId: number): Promise<ApiResult<string>> {
        return this.post<string>(`api/sms/history/export-details/${batchId}`);
    }
    public async SMSExportHistory(batchId: number): Promise<ApiResult<string>> {
        return this.post<string>('api/sms/history/export');
    }
    public async GetGroupsCounting(
        groups: number[],
        setting: { withSMS?: boolean; withEmail?: boolean } | null = null
    ): Promise<ApiResult<number>> {
        return this.post<number>(
            'api/groups/count',
            JSON.stringify({
                ContactGroups: groups,
                ...(setting || {}),
            })
        );
    }
    public async SendSMS(template: SendSmsModel): Promise<ApiResult<number>> {
        return this.post<number>('api/sms/send-sms', JSON.stringify(template));
    }
    public async SendTestSMS(template: SendTestSmsModel): Promise<ApiResult<number>> {
        return this.post<number>('api/sms/send-test-sms/', JSON.stringify(template));
    }
    public async GetSMSBatch(id: number): Promise<ApiResult<SendSmsModel>> {
        return this.get<SendSmsModel>('api/sms/batch/get', {
            params: { id },
        });
    }
    public async SendTestEmail(template: SendTestEmailModel): Promise<ApiResult<number>> {
        return this.post<number>('api/email/send-test-email/', JSON.stringify(template));
    }
    public async SendEmail(template: EmailSendingSettings): Promise<ApiResult<number>> {
        return this.post<number>('api/email/send-email', JSON.stringify(template));
    }
    public async GetEmailTemplate(id: number) {
        return this.get<EmailSendingSettings>('api/email/templates/get', {
            params: { id },
        });
    }

    /**
     * Contact list export
     * @param filter - sending ContactFilter
     * @returns job id
     */
    public async ExportContacts(filter: ContactFilter): Promise<ApiResult<string>> {
        return this.post<string>('api/contacts/export', JSON.stringify({ filter }));
    }

    public async UploadExcelImportContacts(f: FormData): Promise<ApiResult<ApiInterfaces.UploadExcelImportContacts>> {
        return this.postFormData<ApiInterfaces.UploadExcelImportContacts>('api/contacts/upload-excel', f);
    }

    public QueueImportProgress(param: GetTaskProgressRequest) {
        return this.get<GetTaskProgressResponse>('/api/queue/progress', { params: param });
    }
    public GetTaskInfo(taskId: string) {
        return this.get<GetTaskInfoResponse>('/api/upload-excel/task-info', { params: { TaskId: taskId } });
    }
    public importValidate(req: ValidateImportRequest) {
        return this.post<ImportValidationResponse>('/api/upload-excel/validate', JSON.stringify(req));
    }
    public importStart(taskId: string, groupId: number | null) {
        return this.post<GetTaskInfoResponse>('/api/upload-excel/import', JSON.stringify({ taskId, groupId }));
    }
    public GetExcelTemplate() {
        return this.get<File>('/api/upload-excel/getExcelTemplate', { responseType: 'arraybuffer' });
    }
    public SaveClientSettings(req: ClientSettings) {
        return this.post<boolean>('/api/client-settings', JSON.stringify(req));
    }
    public GetClientSettings() {
        return this.get<ClientSettings>('/api/client-settings');
    }
    public async UnconnectContactsToTags(contactId: number, tagId: number): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/contact-tags/unconnect', JSON.stringify({ contactId, tagId }));
    }
    public async UpdateContactToTags(contactId: number, tags: number[]): Promise<ApiResult<boolean>> {
        return this.post<boolean>('api/contact-tags/update', JSON.stringify({ contactId, tags }));
    }
    public async CreateEditTag(tag: TagSaveRequest): Promise<ApiResult<number>> {
        return this.post<number>('api/tag/create-update', JSON.stringify(tag));
    }
    public GetSyncAppSettings(): Promise<ApiResult<SyncAppSettings>> {
        return this.get<SyncAppSettings>('/api/sync-app-settings');
    }
    public SaveSyncAppSettings(req: SyncAppSettings): Promise<ApiResult<boolean>> {
        return this.post<boolean>('/api/sync-app-settings', JSON.stringify(req));
    }
    public GetVrmFieldsForSyncAppSettings(): Promise<ApiResult<VrmFieldsForSyncAppResponse>> {
        return this.get<VrmFieldsForSyncAppResponse>('/api/sync-app-settings/vrm-fields');
    }
}

export const api = new Api();
